import React, { useState } from 'react';

// Design Tokens
const gold = "#f5a623";
const blue = "#3b82f6", blue2 = "#60a5fa";
const green = "#22c55e";
const red = "#ef4444";
const purple = "#a78bfa";
const navy3 = "#1a2236", navy4 = "#212d45";
const white = "#ffffff", textCol = "#cbd5e1", muted = "#64748b";
const border = "rgba(255,255,255,0.07)";

// Shared atoms
const s = {
  section: { marginBottom: 48 },
  eyebrow: { display: "flex", alignItems: "center", gap: 12, marginBottom: 12 },
  tag: { fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: blue },
  badge: { fontFamily: "monospace", fontSize: "0.68rem", color: muted },
  h2: { fontFamily: "Georgia,serif", fontSize: "1.75rem", fontWeight: 800, color: white, lineHeight: 1.3, marginBottom: 16 },
  prose: { fontSize: "0.95rem", color: textCol, lineHeight: 1.8, marginBottom: 20 },
  hr: { border: "none", borderTop: `1px solid ${border}`, margin: "40px 0" },
};

function Callout({ icon, children, accent = gold }) {
  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderLeft: `3px solid ${accent}`, borderRadius: 8, padding: "20px 24px", margin: "24px 0", display: "flex", gap: 14, alignItems: "flex-start" }}>
      <span style={{ fontSize: "1.1rem", flexShrink: 0, marginTop: 2 }}>{icon}</span>
      <div style={{ fontSize: "0.88rem", color: textCol, lineHeight: 1.75 }}>{children}</div>
    </div>
  );
}

function ColCard({ label, labelColor = gold, accent = gold, title, body, items }) {
  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 24, position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg,${accent},transparent)` }} />
      <div style={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: labelColor, marginBottom: 6 }}>{label}</div>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "1.1rem", fontWeight: 800, color: white, marginBottom: body ? 10 : 0 }}>{title}</div>
      {body && <div style={{ fontSize: "0.85rem", color: muted, lineHeight: 1.7 }}>{body}</div>}
      {items && <ul style={{ listStyle: "none", margin: "10px 0 0", padding: 0 }}>{items.map((it, i) => (
        <li key={i} style={{ display: "flex", gap: 8, fontSize: "0.83rem", color: textCol, padding: "6px 0", borderBottom: i < items.length - 1 ? `1px solid ${border}` : "none" }}>
          <span style={{ color: gold, flexShrink: 0, fontSize: "0.75rem", marginTop: 2 }}>→</span>{it}
        </li>
      ))}</ul>}
    </div>
  );
}

function FeatCard({ icon, title, body }) {
  const [hov, setHov] = useState(false);
  return (
    <div onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ background: navy3, border: `1px solid ${hov ? "rgba(59,130,246,.35)" : border}`, borderRadius: 8, padding: "22px 20px", transition: "all 0.2s", transform: hov ? "translateY(-2px)" : "none" }}>
      <div style={{ fontSize: "1.4rem", marginBottom: 10 }}>{icon}</div>
      <div style={{ fontSize: "0.85rem", fontWeight: 700, color: white, marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: "0.82rem", color: muted, lineHeight: 1.65 }}>{body}</div>
    </div>
  );
}

// Business Cycle
const cyclePhases = [
  { label: "Expansion", icon: "📈", body: "The economy is growing steadily. Employment is rising, corporate profits are climbing, inflation is stable, and GDP is increasing. Consumer and business confidence are high.", indicators: [{ text: "↑ GDP", type: "up" }, { text: "↑ Corporate Profits", type: "up" }, { text: "↑ Employment", type: "up" }, { text: "Stable Inflation", type: "neutral" }, { text: "↑ Consumer Confidence", type: "up" }] },
  { label: "Peak", icon: "🔺", body: "The top of the cycle. Demand has outstripped supply, creating labour shortages and upward wage pressure. Inflation rises and interest rates climb as the central bank responds. This is the turning point.", indicators: [{ text: "↑ Inflation", type: "up" }, { text: "↑ Interest Rates", type: "up" }, { text: "↑ Wages", type: "up" }, { text: "Labour Shortages", type: "down" }, { text: "GDP at Maximum", type: "neutral" }] },
  { label: "Contraction", icon: "📉", body: "Real GDP is declining. Inventories pile up as sales slow and firms begin laying off workers. Two consecutive quarters of declining real GDP equals a Recession. Confidence erodes.", indicators: [{ text: "↓ Real GDP", type: "down" }, { text: "↓ Employment", type: "down" }, { text: "↓ Profits", type: "down" }, { text: "↑ Inventory", type: "up" }, { text: "↓ Consumer Spending", type: "down" }] },
  { label: "Trough", icon: "⬇️", body: "The bottom of the cycle. Economic activity is at its lowest. Interest rates fall as the central bank stimulates. Inflation cools. Eventually consumers begin spending again, setting the stage for recovery.", indicators: [{ text: "↓ Interest Rates", type: "down" }, { text: "↓ Inflation", type: "down" }, { text: "GDP Stabilizing", type: "neutral" }, { text: "Consumers Cautious", type: "neutral" }] },
  { label: "Recovery", icon: "🔄", body: "GDP begins climbing back toward previous peak levels. Firms increase production but remain cautious about hiring — they increase hours for existing staff before creating new jobs.", indicators: [{ text: "↑ GDP", type: "up" }, { text: "Cautious Hiring", type: "neutral" }, { text: "↑ Production", type: "up" }, { text: "↑ Consumer Spending", type: "up" }] },
];

function CiTag({ text, type }) {
  const colors = { up: [green, "rgba(34,197,94,.1)", "rgba(34,197,94,.2)"], down: ["#f87171", "rgba(239,68,68,.1)", "rgba(239,68,68,.2)"], neutral: [gold, "rgba(245,166,35,.12)", "rgba(245,166,35,.2)"] };
  const [col, bg, bdr] = colors[type];
  return <span style={{ fontSize: "0.75rem", fontWeight: 700, padding: "3px 10px", borderRadius: 4, color: col, background: bg, border: `1px solid ${bdr}`, marginRight: 6, marginBottom: 6, display: "inline-block" }}>{text}</span>;
}

function BusinessCycle() {
  const [active, setActive] = useState(0);
  const ph = cyclePhases[active];
  return (
    <div style={{ margin: "24px 0" }}>
      <div style={{ display: "flex", gap: 0, marginBottom: 4 }}>
        {cyclePhases.map((p, i) => (
          <div key={i} onClick={() => setActive(i)} style={{ flex: 1, padding: "0 4px", cursor: "pointer" }}>
            <div style={{ background: active === i ? navy4 : navy3, border: `1px solid ${active === i ? "rgba(245,166,35,.35)" : border}`, borderBottom: active === i ? `2px solid ${gold}` : `1px solid ${border}`, borderRadius: "6px 6px 0 0", padding: "10px 6px", textAlign: "center", transition: "all 0.2s" }}>
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: active === i ? gold : muted, margin: "0 auto 6px", boxShadow: active === i ? "0 0 8px rgba(245,166,35,.5)" : "none", transition: "all 0.2s" }} />
              <div style={{ fontSize: "0.68rem", fontWeight: 700, color: active === i ? gold : muted, letterSpacing: "0.06em", textTransform: "uppercase" }}>{p.label}</div>
            </div>
          </div>
        ))}
      </div>
      <div style={{ background: navy4, border: "1px solid rgba(245,166,35,.15)", borderRadius: "0 0 8px 8px", padding: "22px 26px" }}>
        <div style={{ fontFamily: "Georgia,serif", fontSize: "1.1rem", color: white, marginBottom: 10 }}>{ph.icon} {ph.label}</div>
        <p style={{ fontSize: "0.88rem", color: textCol, lineHeight: 1.8, marginBottom: 14 }}>{ph.body}</p>
        <div>{ph.indicators.map((ind, i) => <CiTag key={i} {...ind} />)}</div>
      </div>
    </div>
  );
}

// Economic Indicators
const indicators = [
  { type: "leading", badge: "Leading", title: "Predict Future Activity", sub: "Change before the economy changes. Used to anticipate turning points.", arrow: "↗", color: green, bg: "rgba(34,197,94,.1)", bdr: "rgba(34,197,94,.2)", examples: ["Housing starts", "Manufacturer's new orders", "Stock market prices", "Money supply (M2)"] },
  { type: "coincident", badge: "Coincident", title: "Move With the Economy", sub: "Change at the same time — they confirm what's happening now.", arrow: "→", color: gold, bg: "rgba(245,166,35,.12)", bdr: "rgba(245,166,35,.2)", examples: ["Personal income", "GDP itself", "Retail sales", "Industrial production"] },
  { type: "lagging", badge: "Lagging", title: "Confirm Past Changes", sub: "Change after the economy has already shifted. Useful for confirmation.", arrow: "↘", color: purple, bg: "rgba(167,139,250,.1)", bdr: "rgba(167,139,250,.2)", examples: ["Unemployment rate", "Inflation rate (CPI)", "Labour costs", "Outstanding loans"] },
];

function IndicatorCards() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 my-6">
      {indicators.map((ind, i) => (
        <div key={i} style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: "20px" }}>
          <span style={{ display: "inline-block", fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", padding: "3px 10px", borderRadius: 100, color: ind.color, background: ind.bg, border: `1px solid ${ind.bdr}`, marginBottom: 10 }}>{ind.badge}</span>
          <div style={{ fontFamily: "Georgia,serif", fontSize: "0.95rem", color: white, marginBottom: 4 }}>{ind.title}</div>
          <div style={{ fontSize: "0.78rem", color: muted, marginBottom: 12, lineHeight: 1.5 }}>{ind.sub}</div>
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {ind.examples.map((ex, j) => (
              <li key={j} style={{ display: "flex", gap: 8, fontSize: "0.8rem", color: textCol, padding: "5px 0", borderBottom: j < ind.examples.length - 1 ? `1px solid ${border}` : "none" }}>
                <span style={{ color: ind.color, flexShrink: 0 }}>{ind.arrow}</span>{ex}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

// Unemployment Tabs
const unempTypes = [
  { label: "🔄 Cyclical", title: "Cyclical Unemployment", body: "Directly tied to the business cycle. When the economy contracts, demand falls and employers lay off workers. When the economy expands, these jobs return.", example: "A manufacturing worker laid off during a recession when car sales plummet.", pills: [{ text: "Business Cycle Driven", col: gold }, { text: "Rises in Recessions", col: "#f87171" }, { text: "Falls in Expansions", col: green }] },
  { label: "🌨️ Seasonal", title: "Seasonal Unemployment", body: "Occurs in industries that only operate during certain parts of the year. Workers are unemployed during the off-season by the predictable nature of their industry.", example: "Ski resort staff unemployed in summer; agricultural workers idle in winter.", pills: [{ text: "Predictable Pattern", col: gold }, { text: "Industry-Specific", col: blue2 }] },
  { label: "↔️ Frictional", title: "Frictional Unemployment", body: "The natural, normal unemployment that exists even in a healthy economy. People between jobs — voluntarily moving to a better position or just entering the workforce.", example: "A recent university graduate searching for their first job; a worker who quit for something better.", pills: [{ text: "Normal & Healthy", col: gold }, { text: "Temporary", col: green }, { text: "Job Transition", col: blue2 }] },
  { label: "🔧 Structural", title: "Structural Unemployment", body: "A fundamental mismatch between the skills workers have and the skills employers need. Caused by technological change, automation, or industrial decline. Hardest type to resolve — requires retraining.", example: "A coal miner whose mine closes permanently; a factory worker replaced by automation.", pills: [{ text: "Skills Mismatch", col: "#f87171" }, { text: "Long-Term", col: "#f87171" }, { text: "Requires Retraining", col: blue2 }] },
];

function UnemploymentTabs() {
  const [active, setActive] = useState(0);
  const ut = unempTypes[active];
  return (
    <div style={{ margin: "24px 0" }}>
      <div style={{ display: "flex", gap: 2, flexWrap: "wrap", marginBottom: 0 }}>
        {unempTypes.map((u, i) => (
          <button key={i} onClick={() => setActive(i)} style={{ padding: "10px 16px", background: active === i ? navy4 : navy3, border: `1px solid ${active === i ? "rgba(245,166,35,.25)" : border}`, borderBottom: active === i ? `2px solid ${gold}` : `1px solid ${border}`, borderRadius: "6px 6px 0 0", cursor: "pointer", fontFamily: "inherit", fontSize: "0.78rem", fontWeight: 700, color: active === i ? gold : muted, transition: "all 0.2s" }}>{u.label}</button>
        ))}
      </div>
      <div style={{ background: navy4, border: "1px solid rgba(245,166,35,.15)", borderRadius: "0 0 8px 8px", padding: 26 }}>
        <div style={{ fontFamily: "Georgia,serif", fontSize: "1.1rem", color: white, marginBottom: 10 }}>{ut.title}</div>
        <p style={{ fontSize: "0.9rem", color: textCol, lineHeight: 1.8, marginBottom: 10 }}>{ut.body}</p>
        <p style={{ fontSize: "0.85rem", color: muted, fontStyle: "italic", marginBottom: 14 }}>Example: {ut.example}</p>
        <div>{ut.pills.map((p, i) => <span key={i} style={{ display: "inline-block", fontSize: "0.75rem", fontWeight: 700, padding: "3px 10px", borderRadius: 4, color: p.col, background: `${p.col}18`, border: `1px solid ${p.col}33`, marginRight: 4, marginBottom: 4 }}>{p.text}</span>)}</div>
      </div>
    </div>
  );
}

// Inflation Cards
const inflTypes = [
  { icon: "⬆️", name: "Cost-Push", def: "Rising production costs (oil, wages) force businesses to raise prices. Supply-side pressure pushes the cost curve upward." },
  { icon: "💸", name: "Demand-Pull", def: '"Too much money chasing too few goods." Strong consumer demand pulls prices upward — an overheating economy.' },
  { icon: "🔽", name: "Disinflation", def: "Prices are still rising, but the rate of increase is slowing. e.g., from 5% to 3%. Not the same as deflation." },
  { icon: "📉", name: "Deflation", def: "A general drop in prices — negative inflation. Dangerous: hurts corporate profits and increases the real burden of debt." },
];

function InflationCardItem({ it }) {
  const [hov, setHov] = useState(false);
  return (
    <div onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ background: navy3, border: `1px solid ${hov ? "rgba(245,166,35,.3)" : border}`, borderRadius: 8, padding: 20, textAlign: "center", transition: "all 0.2s", transform: hov ? "translateY(-2px)" : "none" }}>
      <div style={{ fontSize: "1.8rem", marginBottom: 10 }}>{it.icon}</div>
      <div style={{ fontSize: "0.78rem", fontWeight: 700, color: white, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.06em" }}>{it.name}</div>
      <div style={{ fontSize: "0.78rem", color: muted, lineHeight: 1.6 }}>{it.def}</div>
    </div>
  );
}

function InflationCards() {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 my-6">
      {inflTypes.map((it, i) => (
        <InflationCardItem key={i} it={it} />
      ))}
    </div>
  );
}

// Policy Cards
function PolicyCards() {
  return (
    <div className="grid md:grid-cols-2 gap-4 my-6">
      {[
        { accent: blue, label: "Managed by the Bank of Canada", title: "Monetary Policy", rows: [["Goal", <>Keep inflation low and stable — target range of <strong style={{ color: blue2 }}>1% to 3%</strong></>], ["Key Tool", <>Target for the <strong style={{ color: blue2 }}>Overnight Rate</strong> — what financial institutions charge each other for one-day loans</>], ["Expansionary", "Lower rates → cheaper borrowing → more spending → stimulates growth"], ["Contractionary", "Raise rates → costlier borrowing → less spending → cools inflation"], ["Bank Rate", "The rate the Bank of Canada charges commercial banks — the top of the operating band"]] },
        { accent: gold, label: "Managed by the Government", title: "Fiscal Policy", rows: [["Tools", <><strong style={{ color: gold }}>Taxation</strong> and <strong style={{ color: gold }}>Government Spending</strong></>], ["Expansionary", "Cut taxes or increase spending → runs a deficit → stimulates demand and growth"], ["Contractionary", "Raise taxes or cut spending → runs a surplus → slows the economy"], ["Key Note", "Fiscal policy is set by elected government — subject to political constraints that monetary policy avoids"]] },
      ].map((card, ci) => (
        <div key={ci} style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 24, position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg,${card.accent},transparent)` }} />
          <div style={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: card.accent, marginBottom: 6 }}>{card.label}</div>
          <div style={{ fontFamily: "Georgia,serif", fontSize: "1.15rem", fontWeight: 800, color: white, marginBottom: 14 }}>{card.title}</div>
          <div className="grid gap-2">
            {card.rows.map(([lbl, val], ri) => (
              <div key={ri} style={{ background: navy4, borderRadius: 6, padding: "12px 14px" }}>
                <div style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: muted, marginBottom: 4 }}>{lbl}</div>
                <div style={{ fontSize: "0.85rem", color: textCol, lineHeight: 1.6 }}>{val}</div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// Calculator Components
function CalcField({ label, value, onChange }) {
  return (
    <div>
      <label style={{ display: "block", fontSize: "0.72rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>{label}</label>
      <input type="number" value={value} onChange={e => onChange(e.target.value)} style={{ width: "100%", background: navy4, border: `1px solid ${border}`, borderRadius: 6, padding: "11px 13px", color: white, fontFamily: "monospace", fontSize: "0.92rem", outline: "none" }} />
    </div>
  );
}

function CalcBox({ title, formula, children }) {
  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 28, margin: "24px 0" }}>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "1rem", color: white, marginBottom: 6 }}>{title}</div>
      <div style={{ fontFamily: "monospace", fontSize: "0.82rem", color: gold, background: "rgba(245,166,35,.12)", border: "1px solid rgba(245,166,35,.2)", padding: "6px 12px", borderRadius: 4, display: "inline-block", marginBottom: 20 }}>{formula}</div>
      {children}
    </div>
  );
}

function CalcResult({ label, value, sub, steps }) {
  if (!value) return null;
  return (
    <div style={{ background: navy4, border: "1px solid rgba(245,166,35,.2)", borderRadius: 8, padding: 20, marginTop: 16 }}>
      <div style={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: gold, marginBottom: 6 }}>{label}</div>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "2rem", fontWeight: 900, color: white }}>{value}</div>
      {sub && <div style={{ fontSize: "0.82rem", color: muted, marginTop: 4 }}>{sub}</div>}
      {steps && (
        <ul style={{ listStyle: "none", padding: 0, margin: "14px 0 0", borderTop: `1px solid ${border}`, paddingTop: 12 }}>
          {steps.map((step, i) => (
            <li key={i} style={{ fontFamily: "monospace", fontSize: "0.79rem", color: textCol, padding: "4px 0", display: "flex", gap: 8 }}>
              <span style={{ color: gold, flexShrink: 0 }}>→</span>{step}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function GDPCalc() {
  const [c, setC] = useState(1200), [i, setI] = useState(400), [g, setG] = useState(600), [x, setX] = useState(500), [m, setM] = useState(450);
  const [result, setResult] = useState(null);
  function calc() {
    const cv = parseFloat(c) || 0, iv = parseFloat(i) || 0, gv = parseFloat(g) || 0, xv = parseFloat(x) || 0, mv = parseFloat(m) || 0;
    const netEx = xv - mv, gdp = cv + iv + gv + netEx;
    setResult({ value: `$${gdp.toLocaleString("en-CA")}B`, sub: `Net exports: ${netEx >= 0 ? "+" : ""}$${netEx}B — ${netEx >= 0 ? "Trade Surplus" : "Trade Deficit"}`, steps: [`C = $${cv}B + I = $${iv}B + G = $${gv}B + (X−M) = ($${xv}B − $${mv}B)`, `= $${cv + iv + gv}B + $${netEx}B`, `GDP = $${gdp.toLocaleString()}B`] });
  }
  return (
    <CalcBox title="🧮 GDP Expenditure Calculator" formula="GDP = C + I + G + (X − M)">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <CalcField label="Consumption (C) $B" value={c} onChange={setC} />
        <CalcField label="Investment (I) $B" value={i} onChange={setI} />
        <CalcField label="Gov. Spending (G) $B" value={g} onChange={setG} />
        <CalcField label="Exports (X) $B" value={x} onChange={setX} />
        <CalcField label="Imports (M) $B" value={m} onChange={setM} />
      </div>
      <button onClick={calc} style={{ background: gold, color: "#0b0f1a", border: "none", padding: "12px 28px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>Calculate GDP →</button>
      {result && <CalcResult label="GDP (Expenditure Approach)" {...result} />}
    </CalcBox>
  );
}

function InflationCalc() {
  const [prev, setPrev] = useState(153.1), [curr, setCurr] = useState(158.3), [result, setResult] = useState(null);
  function calc() {
    const p = parseFloat(prev), c = parseFloat(curr);
    if (!p) return;
    const diff = c - p, rate = (diff / p * 100).toFixed(2);
    setResult({ value: `${rate}%`, sub: rate > 0 ? "Prices are rising — purchasing power is declining" : "Prices are falling — deflation territory", steps: [`Difference: ${c} − ${p} = ${diff.toFixed(1)}`, `Divide: ${diff.toFixed(1)} ÷ ${p} = ${(diff / p).toFixed(5)}`, `Multiply by 100: ${rate}%`] });
  }
  return (
    <CalcBox title="🧮 Inflation Rate Calculator" formula="Inflation Rate = ((Current CPI − Previous CPI) / Previous CPI) × 100">
      <div className="grid grid-cols-2 gap-3 mb-4">
        <CalcField label="Previous Year CPI" value={prev} onChange={setPrev} />
        <CalcField label="Current Year CPI" value={curr} onChange={setCurr} />
      </div>
      <button onClick={calc} style={{ background: gold, color: "#0b0f1a", border: "none", padding: "12px 28px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>Calculate Inflation Rate →</button>
      {result && <CalcResult label="Annual Inflation Rate" {...result} />}
    </CalcBox>
  );
}

function RealReturnCalc() {
  const [nom, setNom] = useState(6), [inf, setInf] = useState(2.5), [result, setResult] = useState(null);
  function calc() {
    const real = (parseFloat(nom) - parseFloat(inf)).toFixed(2);
    setResult({ value: `${real}%`, sub: real > 0 ? "Positive real return — purchasing power is growing" : "Negative real return — inflation is outpacing your investment", steps: [`Nominal Return: ${nom}%`, `Inflation Rate: ${inf}%`, `Real Return = ${nom}% − ${inf}% = ${real}%`] });
  }
  return (
    <CalcBox title="🧮 Real Rate of Return Calculator" formula="Real Return ≈ Nominal Return − Inflation Rate">
      <div className="grid grid-cols-2 gap-3 mb-4">
        <CalcField label="Nominal Return (%)" value={nom} onChange={setNom} />
        <CalcField label="Inflation Rate (%)" value={inf} onChange={setInf} />
      </div>
      <button onClick={calc} style={{ background: gold, color: "#0b0f1a", border: "none", padding: "12px 28px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>Calculate Real Return →</button>
      {result && <CalcResult label="Real Rate of Return" {...result} />}
    </CalcBox>
  );
}

function RealGDPCalc() {
  const [ngdp, setNgdp] = useState(4.4), [inf, setInf] = useState(1.1), [result, setResult] = useState(null);
  function calc() {
    const real = (parseFloat(ngdp) - parseFloat(inf)).toFixed(2);
    setResult({ value: `${real}%`, sub: real > 0 ? "The economy actually produced more goods and services" : "Negative real growth — production contracted in real terms", steps: [`Nominal GDP Growth: ${ngdp}%`, `Inflation Rate: ${inf}%`, `Real GDP Growth = ${ngdp}% − ${inf}% = ${real}%`] });
  }
  return (
    <CalcBox title="🧮 Real GDP Growth Calculator" formula="Real GDP Growth ≈ Nominal GDP Growth − Inflation Rate">
      <div className="grid grid-cols-2 gap-3 mb-4">
        <CalcField label="Nominal GDP Growth (%)" value={ngdp} onChange={setNgdp} />
        <CalcField label="Inflation Rate (%)" value={inf} onChange={setInf} />
      </div>
      <button onClick={calc} style={{ background: gold, color: "#0b0f1a", border: "none", padding: "12px 28px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>Calculate Real GDP Growth →</button>
      {result && <CalcResult label="Real GDP Growth Rate" {...result} />}
    </CalcBox>
  );
}

// Quiz
const questions = [
  { q: "Which of the following is considered a 'Leading Indicator' of future economic activity?", opts: ["Unemployment rate", "Inflation rate (CPI)", "Housing starts", "GDP"], correct: 2, fb: { y: "Correct! Housing starts are a leading indicator — they rise before the broader economy improves because construction activity predicts future spending.", n: "Not quite. Housing starts are a leading indicator. Unemployment and inflation are lagging. GDP is a coincident indicator." } },
  { q: "The CPI was 120 last year. This year it is 126. What is the inflation rate?", opts: ["4.0%", "5.0%", "6.0%", "0.5%"], correct: 1, fb: { y: "Correct! ((126 − 120) / 120) × 100 = (6 / 120) × 100 = 5.0%.", n: "Not quite. Formula: ((Current − Previous) / Previous) × 100 = ((126 − 120) / 120) × 100 = 5.0%." } },
  { q: "Which type of unemployment results from a mismatch between the skills workers have and the jobs available?", opts: ["Cyclical unemployment", "Frictional unemployment", "Seasonal unemployment", "Structural unemployment"], correct: 3, fb: { y: "Correct! Structural unemployment is caused by a skills or geographic mismatch — it cannot be solved by economic recovery alone. Retraining is required.", n: "Not quite. Structural unemployment is the skills mismatch. Cyclical is tied to the business cycle; frictional is normal job transition; seasonal is industry-specific." } },
  { q: "A 'Recession' is technically defined as:", opts: ["Two consecutive months of declining nominal GDP", "Two consecutive quarters of declining real GDP", "A rise in both interest rates and inflation simultaneously", "A decrease in the government budget deficit"], correct: 1, fb: { y: "Correct! A recession is two consecutive quarters (not months) of declining real (not nominal) GDP.", n: "Not quite. The precise definition is two consecutive quarters of declining real GDP — both the unit (quarters) and the measure (real, not nominal) matter." } },
  { q: "If the Bank of Canada wants to implement 'Expansionary' monetary policy, what action would it most likely take?", opts: ["Sell government bonds on the open market", "Raise the Bank Rate", "Lower the Target for the Overnight Rate", "Increase personal income taxes"], correct: 2, fb: { y: "Correct! Lowering the overnight rate makes borrowing cheaper, encouraging spending and investment — the hallmark of expansionary monetary policy.", n: "Not quite. Expansionary monetary policy = lowering rates. Raising rates is contractionary. Taxes are fiscal policy, not monetary." } },
  { q: "Real GDP is best defined as:", opts: ["The value of goods and services produced, adjusted for inflation", "The value of goods produced at current market prices", "Total exports minus total imports for the year", "The standard of living of the average citizen"], correct: 0, fb: { y: "Correct! Real GDP is nominal GDP adjusted for inflation — it measures the actual volume of production, not just changes in price levels.", n: "Not quite. Real GDP strips out inflation to reveal true production growth. Nominal GDP includes the effect of price changes." } },
  { q: "If the nominal interest rate on a GIC is 7% and the annual inflation rate is 3%, what is the investor's real rate of return?", opts: ["10%", "4%", "2.3%", "7%"], correct: 1, fb: { y: "Correct! Real Return = Nominal − Inflation = 7% − 3% = 4%. This represents the actual increase in purchasing power.", n: "Not quite. Real Return = Nominal − Inflation = 7% − 3% = 4%. Adding them gives the wrong answer." } },
  { q: "Which of the following actions falls under 'Fiscal Policy'?", opts: ["The Bank of Canada raising interest rates", "The Government reducing personal income taxes", "The Bank of Canada buying government bonds", "A change in the Canadian dollar exchange rate"], correct: 1, fb: { y: "Correct! Fiscal policy = government taxes and spending. Cutting taxes is a classic expansionary fiscal tool.", n: "Not quite. Fiscal policy = government taxes and spending. Everything involving the Bank of Canada and interest rates is monetary policy." } },
  { q: "The 'Bank Rate' is best defined as:", opts: ["The rate Canadian banks charge their best corporate customers", "The inflation rate plus 2%", "The rate the Bank of Canada charges on loans to commercial banks — the top of the operating band", "The rate on a standard 5-year mortgage"], correct: 2, fb: { y: "Correct! The Bank Rate is the rate the Bank of Canada charges commercial banks — it is the ceiling of the operating band, above the overnight rate.", n: "Not quite. The Bank Rate is the rate the Bank of Canada charges commercial banks — it sits at the top of the operating band for the overnight rate." } },
  { q: "In the Balance of Payments, the 'Current Account' primarily tracks:", opts: ["Foreign direct investment into Canadian factories", "The purchase and sale of stocks and bonds by foreigners", "The import and export of goods and services, and investment income", "The total outstanding government debt"], correct: 2, fb: { y: "Correct! The Current Account tracks trade in goods and services (imports/exports) and investment income flows.", n: "Not quite. Foreign investment and securities purchases are in the Capital & Financial Account. The Current Account covers goods, services, and investment income." } },
];

function Quiz() {
  const [current, setCurrent] = useState(0);
  const [answered, setAnswered] = useState({});
  const [done, setDone] = useState(false);
  const score = Object.values(answered).filter(a => a.correct).length;
  const total = questions.length;

  function answer(idx) {
    if (answered[current] !== undefined) return;
    setAnswered({ ...answered, [current]: { chosen: idx, correct: idx === questions[current].correct } });
  }
  function next() { if (current < total - 1) setCurrent(current + 1); else setDone(true); }
  function retry() { setCurrent(0); setAnswered({}); setDone(false); }

  const q = questions[current];
  const ans = answered[current];
  const pct = Math.round(score / total * 100);

  return (
    <div style={{ background: "#111827", border: `1px solid ${border}`, borderRadius: 12, padding: 28, marginTop: 24 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <div style={{ fontFamily: "Georgia,serif", fontSize: "1.2rem", color: white }}>Chapter 3 Quiz</div>
        <div style={{ fontFamily: "monospace", fontSize: "0.78rem", color: muted }}>Question {current + 1} of {total}</div>
      </div>
      <div style={{ height: 4, background: navy4, borderRadius: 2, marginBottom: 24, overflow: "hidden" }}>
        <div style={{ height: "100%", background: `linear-gradient(90deg,${gold},${blue})`, borderRadius: 2, width: `${(current + 1) / total * 100}%`, transition: "width 0.4s ease" }} />
      </div>

      {!done ? (
        <>
          <div style={{ fontFamily: "Georgia,serif", fontSize: "1rem", color: white, lineHeight: 1.55, marginBottom: 20 }}>{q.q}</div>
          <div className="grid gap-3">
            {q.opts.map((opt, i) => {
              const isChosen = ans?.chosen === i, isCorrect = i === q.correct;
              let bg = navy3, bdr = border, col = textCol;
              if (ans) { if (isCorrect) { bg = "rgba(34,197,94,.08)"; bdr = "#22c55e"; col = "#4ade80"; } else if (isChosen) { bg = "rgba(239,68,68,.08)"; bdr = "#ef4444"; col = "#f87171"; } }
              return (
                <button key={i} onClick={() => answer(i)} disabled={!!ans}
                  style={{ background: bg, border: `1px solid ${bdr}`, borderRadius: 8, padding: "14px 18px", textAlign: "left", fontFamily: "inherit", fontSize: "0.88rem", color: col, cursor: ans ? "default" : "pointer", transition: "all 0.2s", display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ width: 26, height: 26, flexShrink: 0, background: navy4, border: `1px solid ${border}`, borderRadius: 5, display: "inline-flex", alignItems: "center", justifyContent: "center", fontFamily: "monospace", fontSize: "0.72rem", color: muted }}>
                    {["A","B","C","D"][i]}
                  </span>
                  {opt}
                </button>
              );
            })}
          </div>
          {ans && (
            <>
              <div style={{ marginTop: 14, padding: "13px 16px", borderRadius: 7, fontSize: "0.84rem", lineHeight: 1.65, background: ans.correct ? "rgba(34,197,94,.07)" : "rgba(239,68,68,.07)", border: `1px solid ${ans.correct ? "rgba(34,197,94,.25)" : "rgba(239,68,68,.2)"}`, color: ans.correct ? "#86efac" : "#fca5a5" }}>
                {ans.correct ? "✓ " : "✗ "}{ans.correct ? q.fb.y : q.fb.n}
              </div>
              <button onClick={next} style={{ marginTop: 20, background: gold, color: "#0b0f1a", border: "none", padding: "12px 28px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>
                {current < total - 1 ? "Next Question →" : "See My Results →"}
              </button>
            </>
          )}
        </>
      ) : (
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <div style={{ width: 110, height: 110, borderRadius: "50%", margin: "0 auto 20px", background: `conic-gradient(${gold} 0% ${pct}%, ${navy4} ${pct}%)`, display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
            <div style={{ position: "absolute", inset: 14, borderRadius: "50%", background: "#111827" }} />
            <span style={{ position: "relative", fontFamily: "Georgia,serif", fontSize: "1.5rem", fontWeight: 900, color: gold, zIndex: 1 }}>{score}/{total}</span>
          </div>
          <div style={{ fontFamily: "Georgia,serif", fontSize: "1.4rem", color: white, marginBottom: 8 }}>
            {score === total ? "Perfect Score! 🏆" : score >= 8 ? "Strong Result ✦" : score >= 6 ? "Good Effort" : "Keep Studying"}
          </div>
          <div style={{ fontSize: "0.9rem", color: muted, marginBottom: 24 }}>
            {score === total ? "Flawless. Complete mastery of Chapter 3." : score >= 8 ? "Excellent. Review the questions you missed." : score >= 6 ? "Solid foundation. Revisit the business cycle and policy sections." : "Go back through the content — focus on indicators, inflation types, and policy tools."}
          </div>
          <button onClick={retry} style={{ background: "transparent", color: gold, border: `1px solid rgba(245,166,35,.35)`, padding: "10px 26px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>↺ Try Again</button>
        </div>
      )}
    </div>
  );
}

// Glossary
const glossary = [
  { term: "GDP", def: "Gross Domestic Product — the market value of all final goods and services produced within a country in a specific period." },
  { term: "Nominal GDP", def: "GDP measured at current market prices, including the effect of inflation. Does not reflect actual volume of production." },
  { term: "Real GDP", def: "GDP adjusted for inflation (constant dollars). Measures the actual volume of goods and services produced." },
  { term: "Business Cycle", def: "The recurring pattern of expansion, peak, contraction, trough, and recovery in economic activity." },
  { term: "Recession", def: "Two consecutive quarters of declining real GDP. Characterized by rising unemployment and falling output." },
  { term: "Leading Indicator", def: "An economic measure that changes before the economy changes. Used to predict future turning points (e.g., housing starts)." },
  { term: "Lagging Indicator", def: "An economic measure that changes after the economy has already shifted. Confirms trends (e.g., unemployment rate)." },
  { term: "CPI", def: "Consumer Price Index — measures the cost of a standardized basket of ~600 goods and services. Used to calculate inflation." },
  { term: "Inflation", def: "A sustained trend of rising prices across the economy that erodes purchasing power over time." },
  { term: "Deflation", def: "A general decline in price levels (negative inflation). Dangerous: increases real debt burdens and hurts profits." },
  { term: "Disinflation", def: "A slowdown in the rate of inflation — prices are still rising, just more slowly. Not the same as deflation." },
  { term: "Structural Unemployment", def: "Unemployment caused by a skills or geographic mismatch between workers and available jobs. Requires retraining to resolve." },
  { term: "Cyclical Unemployment", def: "Unemployment tied directly to the business cycle — rises in recessions, falls in expansions." },
  { term: "Monetary Policy", def: "Policy managed by the Bank of Canada using the overnight rate to control inflation and stimulate or cool the economy." },
  { term: "Fiscal Policy", def: "Policy managed by the government using taxation and spending to influence economic activity." },
  { term: "Overnight Rate", def: "The rate at which financial institutions lend to each other for one-day periods. The Bank of Canada's primary policy tool." },
  { term: "Bank Rate", def: "The rate the Bank of Canada charges commercial banks for loans. Sits at the top of the operating band." },
  { term: "Current Account", def: "Part of the Balance of Payments tracking trade in goods/services and investment income flows." },
  { term: "Inverted Yield Curve", def: "When short-term interest rates exceed long-term rates — historically a reliable leading indicator of recession." },
  { term: "Real Rate of Return", def: "The actual increase in purchasing power after inflation. Calculated as: Nominal Return − Inflation Rate." },
];

// Main Component
const Chapter3Interactive = () => {
  return (
    <div className="space-y-8">
      {/* Section 1: Economics */}
      <section style={s.section}>
        <div style={s.eyebrow}>
          <span style={s.tag}>Section 01</span>
          <span style={s.badge}>Foundations</span>
        </div>
        <h2 style={s.h2}>What is Economics?</h2>
        <p style={s.prose}>
          Economics is the social science studying how <span style={{ color: gold, fontWeight: 600 }}>consumers, businesses, and governments</span> make choices to allocate <strong style={{ color: white }}>limited resources</strong> to satisfy unlimited needs. It operates at two levels:
        </p>
        <div className="grid md:grid-cols-2 gap-4 my-6">
          <ColCard label="Micro" labelColor={gold} accent={gold} title="Microeconomics" body="Analyzes the behavior of individual consumers and firms." items={["How a price change affects demand for a product","How minimum wage laws affect hiring in one industry","Why one firm earns more profit than a competitor"]} />
          <ColCard label="Macro" labelColor={blue2} accent={blue} title="Macroeconomics" body="Focuses on the economy as a whole — aggregate behavior." items={["National unemployment rates","Country-wide inflation trends","Interest rate policy and GDP growth"]} />
        </div>
        <p style={s.prose}>
          At the heart of economics is <strong style={{ color: white }}>supply and demand</strong>. Markets naturally move toward an <span style={{ color: gold, fontWeight: 600 }}>equilibrium price</span> — the point where quantity demanded equals quantity supplied.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-6">
          <FeatCard icon="📉" title="Law of Demand" body="As prices rise, quantity demanded by consumers falls. Price and demand move in opposite directions." />
          <FeatCard icon="📈" title="Law of Supply" body="As prices rise, producers are willing to supply more. Price and supply move in the same direction." />
          <FeatCard icon="⚖️" title="Equilibrium Price" body="The price where supply equals demand — buyers and sellers agree and the market clears itself." />
        </div>
      </section>

      <hr style={s.hr} />

      {/* Section 2: GDP */}
      <section style={s.section}>
        <div style={s.eyebrow}>
          <span style={s.tag}>Section 02</span>
          <span style={s.badge}>GDP</span>
        </div>
        <h2 style={s.h2}>Measuring Economic Growth (GDP)</h2>
        <p style={s.prose}>
          <strong style={{ color: white }}>Gross Domestic Product (GDP)</strong> is the market value of all <span style={{ color: gold, fontWeight: 600 }}>final goods and services</span> produced within a country during a specific period. It is the primary measure of an economy's size and health.
        </p>
        <div className="grid md:grid-cols-2 gap-4 my-6">
          <ColCard label="Nominal GDP" labelColor={gold} accent={gold} title="Current Prices" body="Value of production at current market prices. Includes the effect of inflation — a rise could reflect price increases, not actual growth." />
          <ColCard label="Real GDP" labelColor={green} accent={green} title="Inflation-Adjusted" body="Value of production in constant dollars — inflation stripped out. Measures actual volume of production. This is what matters." />
        </div>
        <Callout icon="💡">
          <strong>Key Concept:</strong> If Nominal GDP rises by 5% and inflation is 3%, real economic growth is only approximately 2%. Always look at <strong>Real GDP</strong> to assess genuine economic expansion.
        </Callout>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-6">
          <FeatCard icon="👥" title="Population Growth" body="More workers in the labour force means greater productive capacity for the economy." />
          <FeatCard icon="🏭" title="Capital Stock" body="More equipment, factories, and infrastructure amplify what each worker can produce." />
          <FeatCard icon="💡" title="Technological Improvement" body="Increased efficiency and productivity — doing more with less. The most powerful long-run growth engine." />
        </div>
        <GDPCalc />
      </section>

      <hr style={s.hr} />

      {/* Section 3: Business Cycle */}
      <section style={s.section}>
        <div style={s.eyebrow}>
          <span style={s.tag}>Section 03</span>
          <span style={s.badge}>Business Cycle</span>
        </div>
        <h2 style={s.h2}>The Business Cycle</h2>
        <p style={s.prose}>
          The economy moves in irregular waves called the <span style={{ color: gold, fontWeight: 600 }}>business cycle</span>. Click each phase to understand what's happening in the economy at each stage:
        </p>
        <BusinessCycle />
        <p style={s.prose}>
          Economists use three types of indicators to track and anticipate cycle movements:
        </p>
        <IndicatorCards />
        <Callout icon="🎯" accent={blue}>
          <strong style={{ color: blue2 }}>Exam Tip:</strong> The <strong style={{ color: blue2 }}>unemployment rate</strong> is a lagging indicator — it rises after a recession has started and falls after recovery is underway. <strong style={{ color: blue2 }}>Housing starts</strong> are a leading indicator — they predict future economic activity. This distinction is heavily tested.
        </Callout>
      </section>

      <hr style={s.hr} />

      {/* Section 4: Labour */}
      <section style={s.section}>
        <div style={s.eyebrow}>
          <span style={s.tag}>Section 04</span>
          <span style={s.badge}>Labour Markets</span>
        </div>
        <h2 style={s.h2}>Labour Markets & Unemployment</h2>
        <p style={s.prose}>
          The <span style={{ color: gold, fontWeight: 600 }}>labour force</span> consists of all employed people plus those unemployed and actively seeking work. The <strong style={{ color: white }}>participation rate</strong> is the share of the working-age population (15+) in the labour force. Not all unemployment is the same — click each type to explore:
        </p>
        <UnemploymentTabs />
      </section>

      <hr style={s.hr} />

      {/* Section 5: Inflation */}
      <section style={s.section}>
        <div style={s.eyebrow}>
          <span style={s.tag}>Section 05</span>
          <span style={s.badge}>Inflation</span>
        </div>
        <h2 style={s.h2}>Inflation</h2>
        <p style={s.prose}>
          <strong style={{ color: white }}>Inflation</strong> is a sustained trend of rising prices across the economy. It erodes <span style={{ color: gold, fontWeight: 600 }}>purchasing power</span>. The primary measurement tool is the <strong style={{ color: white }}>Consumer Price Index (CPI)</strong>, which tracks the cost of a standardized basket of approximately 600 goods and services.
        </p>
        <InflationCards />
        <Callout icon="⚠️" accent={red}>
          <strong style={{ color: "#f87171" }}>Deflation vs. Disinflation:</strong> <strong style={{ color: "#f87171" }}>Disinflation</strong> = inflation is still positive but slowing (good). <strong style={{ color: "#f87171" }}>Deflation</strong> = prices are actually falling (bad). Deflation is dangerous because it reduces corporate profits, increases real debt burdens, and can trigger a deflationary spiral.
        </Callout>
        <InflationCalc />
      </section>

      <hr style={s.hr} />

      {/* Section 6: Interest Rates */}
      <section style={s.section}>
        <div style={s.eyebrow}>
          <span style={s.tag}>Section 06</span>
          <span style={s.badge}>Interest Rates</span>
        </div>
        <h2 style={s.h2}>Interest Rates</h2>
        <p style={s.prose}>
          Interest rates are the <span style={{ color: gold, fontWeight: 600 }}>"price" of credit</span> — what a borrower pays for the use of someone else's capital. They are determined by four key forces:
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 my-6">
          <FeatCard icon="⚖️" title="Supply & Demand for Capital" body="When demand for borrowing exceeds available savings, rates rise. When capital is abundant, rates fall." />
          <FeatCard icon="⚠️" title="Default Risk" body="Borrowers with higher credit risk pay higher rates. Lenders demand a risk premium to compensate for potential default." />
          <FeatCard icon="🌐" title="Foreign Interest Rates" body="High foreign rates attract capital away from Canada, putting upward pressure on domestic rates." />
          <FeatCard icon="📊" title="Inflation Expectations" body="Lenders demand higher rates to compensate for the expected erosion of purchasing power over the loan term." />
        </div>
        <Callout icon="📐">
          <strong>The Inverted Yield Curve:</strong> Normally, long-term bonds yield more than short-term ones. When short-term rates are <em>higher</em> than long-term rates, the curve is <strong>inverted</strong> — and this has historically been a reliable signal of a coming recession.
        </Callout>
      </section>

      <hr style={s.hr} />

      {/* Section 7: Policy */}
      <section style={s.section}>
        <div style={s.eyebrow}>
          <span style={s.tag}>Section 07</span>
          <span style={s.badge}>Policy</span>
        </div>
        <h2 style={s.h2}>Monetary & Fiscal Policy</h2>
        <p style={s.prose}>
          Governments and central banks use two levers to manage the economy. Understanding who controls what — and how each works — is essential for the exam:
        </p>
        <PolicyCards />
        <Callout icon="🎯" accent={blue}>
          <strong style={{ color: blue2 }}>Critical Distinction:</strong> <strong style={{ color: blue2 }}>Monetary policy</strong> = Bank of Canada + interest rates. <strong style={{ color: blue2 }}>Fiscal policy</strong> = Government + taxes and spending. Any question about "raising interest rates" or "the overnight rate" is monetary policy. Any question about "cutting taxes" or "increasing spending" is fiscal policy.
        </Callout>
      </section>

      <hr style={s.hr} />

      {/* Section 8: International */}
      <section style={s.section}>
        <div style={s.eyebrow}>
          <span style={s.tag}>Section 08</span>
          <span style={s.badge}>International</span>
        </div>
        <h2 style={s.h2}>International Economics & Balance of Payments</h2>
        <p style={s.prose}>
          The <span style={{ color: gold, fontWeight: 600 }}>Balance of Payments</span> is a record of all economic transactions between residents of Canada and the rest of the world. It has two main accounts:
        </p>
        <div className="grid md:grid-cols-2 gap-4 my-6">
          <ColCard label="What We Spend" labelColor={gold} accent={gold} title="Current Account" body="Records trade in goods/services (imports & exports) and investment income flows." items={["Exports of goods (cars, lumber, oil)","Imports of goods (electronics, consumer products)","Trade in services (tourism, financial services)","Investment income (dividends, interest received/paid)"]} />
          <ColCard label="How We Finance It" labelColor={blue2} accent={blue} title="Capital & Financial Account" body="Records investment flows — how Canada finances any trade deficits or surpluses." items={["Foreign direct investment into Canada","Canadian investment abroad","Purchase/sale of stocks and bonds by foreigners","Government foreign borrowing"]} />
        </div>
        <Callout icon="🌐" accent={green}>
          <strong style={{ color: green }}>The Two-Account Rule:</strong> The current account and capital account must balance. A current account deficit (we import more than we export) must be financed by a capital account surplus (foreigners invest more in Canada than we invest abroad).
        </Callout>
      </section>

      <hr style={s.hr} />

      {/* Calculators */}
      <section style={s.section}>
        <div style={s.eyebrow}>
          <span style={s.tag}>Interactive Tools</span>
          <span style={s.badge}>Formulas & Practice</span>
        </div>
        <h2 style={s.h2}>Real Rate of Return & Real GDP Calculators</h2>
        <p style={s.prose}>
          Two more essential Chapter 3 calculations. Practice these until they're automatic — they appear frequently on the exam.
        </p>
        <RealReturnCalc />
        <RealGDPCalc />
      </section>

      <hr style={s.hr} />

      {/* Quiz */}
      <section style={s.section}>
        <div style={s.eyebrow}>
          <span style={s.tag}>Knowledge Check</span>
          <span style={s.badge}>10 Questions</span>
        </div>
        <h2 style={s.h2}>Test Your Understanding</h2>
        <p style={s.prose}>
          10 questions covering all eight sections of Chapter 3. Detailed feedback on every answer.
        </p>
        <Quiz />
      </section>

      <hr style={s.hr} />

      {/* Glossary */}
      <section style={s.section}>
        <div style={s.eyebrow}>
          <span style={s.tag}>Quick Reference</span>
          <span style={s.badge}>Key Terms</span>
        </div>
        <h2 style={s.h2}>Glossary</h2>
        <p style={s.prose}>
          A quick-reference index of all essential terms introduced in Chapter 3.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-6">
          {glossary.map((g, i) => (
            <div key={i} className="bg-slate-800 border border-slate-700 rounded-lg p-4 hover:border-slate-600 transition-all">
              <div style={{ fontFamily: "monospace", fontSize: "0.72rem", color: gold, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6 }}>{g.term}</div>
              <div style={{ fontSize: "0.83rem", color: muted, lineHeight: 1.65 }}>{g.def}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Chapter3Interactive;
